import {
  CheckCardContextProvider,
  useCheckCardContext,
} from "./CheckCardContext";
import { LoadingSkeleton } from "./layouts/LoadingSkeleton";
import { CheckCardProps } from "./types";

import { css } from "@emotion/css";
import { CardWrapper } from "./layouts/CardWrapper";
import { OrderingCheckCardHeader } from "./layouts/OrderingCheckCardHeader";
import { Link, Text } from "@certn/cascadia";
import { useTranslation } from "react-i18next";
import { CheckCardContent } from "./components/CheckCardContent";
import { CheckCardTitle } from "./components/CheckCardTitle";
import { checkHasTemplate } from "./hooks";
import { LearnMoreModal } from "./components/learn-more-modal/LearnMoreModal";
import { List, ListItem, Stack } from "@certn/cascadia-react";

export function OrderingCheckCard(props: CheckCardProps) {
  if (props.isLoading) return <LoadingSkeleton />;

  return (
    <CheckCardContextProvider {...props}>
      <OrderingCheckCardInner />
    </CheckCardContextProvider>
  );
}

function OrderingCheckCardInner() {
  const {
    testID,
    showModal,
    setShowModal,
    width,
    isReadOnly,
    selectedArgument,
    selectedOptions,
    type,
    checkCardConfig,
  } = useCheckCardContext();

  const { t } = useTranslation();
  const hasTemplate = checkHasTemplate(type);

  // In read-only mode, display all selected options if available (for multiple selections like multiple countries)
  const shouldShowMultipleOptions =
    isReadOnly && selectedOptions && selectedOptions.length > 1;

  return (
    <div
      className={css(
        styles.card,
        { maxWidth: width },
        isReadOnly
          ? styles.readonlyCard
          : {
              boxShadow: `inset 0 0 0 ${checkCardConfig.cardBorderSize}`,
              color: `var(--${checkCardConfig.cardBorderColor})`,
              cursor: checkCardConfig.cardHasPointer ? "pointer" : "default",
              "&:hover": {
                boxShadow: checkCardConfig.cardHasHover
                  ? `inset 0 0 0 1px var(--border-input-hover)`
                  : undefined,
              },
            }
      )}
    >
      <CardWrapper>
        <OrderingCheckCardHeader>
          <CheckCardTitle />
        </OrderingCheckCardHeader>
        <CheckCardContent />
      </CardWrapper>
      <Stack className={styles.bottomSection}>
        <Link
          textStyle="global/body-secondary"
          textColor="text-weak"
          onPress={() => setShowModal(true)}
          testID={`${testID}-learn-more-button`}
        >
          {t("Learn more")}
        </Link>
        {shouldShowMultipleOptions ? (
          <div className={styles.optionsListWrapper}>
            <List type="unordered">
              {selectedOptions.map((option, index) => (
                <ListItem key={index}>
                  <Text style="input/label">
                    {hasTemplate
                      ? `${t("Template")}: ${option.label}`
                      : option.label}
                  </Text>
                </ListItem>
              ))}
            </List>
          </div>
        ) : (
          selectedArgument && (
            <Text style="input/label">
              {hasTemplate
                ? `${t("Template")}: ${selectedArgument.label}`
                : selectedArgument.label}
            </Text>
          )
        )}
      </Stack>

      <LearnMoreModal show={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
}

const styles = {
  readonlyCard: css({
    boxShadow: "inset 0 0 0 2px",
    color: "var(--border-input-disabled)",
  }),
  card: css({
    background: "var(--background-base)",
    borderRadius: "var(--border-radius-3)",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    justifyContent: "space-between",
    minWidth: 315,
  }),
  bottomSection: css({
    padding: "var(--space-2) var(--space-4) var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  }),
  optionsListWrapper: css({
    marginTop: "calc(-1 * var(--space-6))",
    marginLeft: "var(--space-4)",
  }),
};
